//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  ContentLibrarySortType,
  MediaFilterParamKey,
  MediaSourceParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import {
  EmptyFilters,
  EmptySources,
} from '@media-library/constants/initialMediaLibraryItemParams';

import { makeStateFactoryQueryParams } from 'app/initialization/routing/stateHelpers';
import { RoutableStateName } from 'app/top-level-states';

export const MediaLibrarySubPageTitles = {
  ALL_MEDIA: 'All Media',
  TEAM_MEDIA: 'Team Uploads',
  MY_MEDIA: 'My Uploads',
} as const;

export const MediaLibraryStateNames: RoutableStateName[] = [
  'library.all-media',
  'library.team-media',
  'library.my-media',
];

export const TrackedSourcesStateNames: RoutableStateName[] = [
  'library.tracked-sources.user',
  'library.tracked-sources.hashtag',
];

export const HashtagsStateNames: RoutableStateName[] = ['library.hashtags'];

export type MediaLibrarySubPages = keyof typeof MediaLibrarySubPageTitles;

// Serialized type for media library url query as `useCurrentStateAndParams` does not include input validation and type conversion functionalities. Further validation and deserialization are required.
export type MediaLibraryQueryParams = Partial<{
  [K in
    | MediaFilterParamKey
    | MediaSourceParamKey
    | typeof MediaLibrarySortTypeQueryKey]: string;
}>;

const mediaFilterKeys: string[] = Object.keys(EmptyFilters);

const mediaSourceKeys: string[] = Object.keys(EmptySources);

export const MediaLibrarySortTypeQueryKey = 'orderBy';

// URL query key searched by universal-image-actions-service and global-uia-laucher to determine whether UIA should be launched and the item to open.
const UIAOpenItemQueryKey = 'openItem';

export const mediaLibraryQueryParams = [
  ...mediaFilterKeys,
  ...mediaSourceKeys,
  MediaLibrarySortTypeQueryKey,
  UIAOpenItemQueryKey,
];

export const mediaLibraryQueryKeys: string = makeStateFactoryQueryParams(
  mediaLibraryQueryParams,
);

export const MediaLibrarySortLabels = {
  [ContentLibrarySortType.TIME]: 'Most Recent',
  [ContentLibrarySortType.LIKES]: 'Most Likes',
  [ContentLibrarySortType.COMMENTS]: 'Most Comments',
  [ContentLibrarySortType.ENGAGEMENT]: 'Most Engaging',
  [ContentLibrarySortType.VISUAL_SCORE]: 'Most On-Brand',
} as const;

export type MediaLibrarySortType = keyof typeof MediaLibrarySortLabels;

export const MediaLibrarySortTypeOptions: MediaLibrarySortType[] = [
  ContentLibrarySortType.TIME,
  ContentLibrarySortType.COMMENTS,
  ContentLibrarySortType.LIKES,
  ContentLibrarySortType.ENGAGEMENT,
  ContentLibrarySortType.VISUAL_SCORE,
];

export const MANAGE_COLLECTIONS_PERMISSION = 'library:manage_collections';
export const CREATE_OR_UPDATE_USER_PERMISSION = 'admin:create_or_update_user';
